import { api } from './api';
import { Holiday, HolidayFormData, HolidayFilters, ApiResponse, PaginationParams } from '../types';

export interface HolidaysResponse {
  holidays: Holiday[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class HolidayService {
  /**
   * Lista todos os feriados com paginação e filtros
   */
  async getAllHolidays(params?: PaginationParams & HolidayFilters): Promise<HolidaysResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.tipo) queryParams.append('tipo', params.tipo);
      if (params?.dataInicio) queryParams.append('dataInicio', params.dataInicio);
      if (params?.dataFim) queryParams.append('dataFim', params.dataFim);

      const url = `/holidays?${queryParams.toString()}`;
      const response = await api.get(url) as ApiResponse<Holiday[]>;
      
      // O interceptor já retorna response.data, então response é diretamente o ApiResponse
      if (response.success && response.data) {
        const result = {
          holidays: response.data,
          pagination: response.pagination || {
            page: params?.page || 1,
            limit: params?.limit || 10,
            total: response.data.length,
            pages: 1,
          },
        };
        
        return result;
      }
      
      throw new Error(response.message || 'Erro ao buscar feriados');
    } catch (error: any) {
      // Se for erro de rede ou timeout, dar mensagem específica
      if (error.code === 'NETWORK_ERROR' || error.message.includes('timeout')) {
        throw new Error('Erro de conexão com o servidor');
      }
      
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Busca um feriado específico por ID
   */
  async getHolidayById(id: number): Promise<Holiday> {
    try {
      const response = await api.get(`/holidays/${id}`) as ApiResponse<Holiday>;
      
      if (response.success && response.data) {
        return response.data;
      }
      
      throw new Error(response.message || 'Feriado não encontrado');
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Cria um novo feriado
   */
  async createHoliday(holidayData: HolidayFormData): Promise<Holiday> {
    try {
      // Validar dados obrigatórios
      if (!holidayData.descricao || !holidayData.tipo || !holidayData.dataInicio || !holidayData.dataFim) {
        throw new Error('Descrição, tipo, data de início e data de fim são obrigatórios');
      }

      // Validar se dataFim é posterior a dataInicio
      const startDate = new Date(holidayData.dataInicio);
      const endDate = new Date(holidayData.dataFim);
      
      if (endDate <= startDate) {
        throw new Error('Data de fim deve ser posterior à data de início');
      }

      const response = await api.post('/holidays', holidayData) as ApiResponse<Holiday>;
      
      if (response.success && response.data) {
        return response.data;
      }
      
      throw new Error(response.message || 'Erro ao criar feriado');
    } catch (error: any) {
      // Tratar erros específicos
      if (error.response?.status === 409) {
        throw new Error('Já existe um feriado do mesmo tipo neste período');
      }
      
      if (error.response?.data?.errors) {
        throw new Error(error.response.data.errors.join(', '));
      }
      
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Atualiza um feriado existente
   */
  async updateHoliday(id: number, holidayData: HolidayFormData): Promise<Holiday> {
    try {
      // Validar se dataFim é posterior a dataInicio
      const startDate = new Date(holidayData.dataInicio);
      const endDate = new Date(holidayData.dataFim);
      
      if (endDate <= startDate) {
        throw new Error('Data de fim deve ser posterior à data de início');
      }

      const response = await api.put(`/holidays/${id}`, holidayData) as ApiResponse<Holiday>;
      
      if (response.success && response.data) {
        return response.data;
      }
      
      throw new Error(response.message || 'Erro ao atualizar feriado');
    } catch (error: any) {
      // Tratar erros específicos
      if (error.response?.status === 404) {
        throw new Error('Feriado não encontrado');
      }
      
      if (error.response?.status === 409) {
        throw new Error('Já existe um feriado do mesmo tipo neste período');
      }
      
      if (error.response?.data?.errors) {
        throw new Error(error.response.data.errors.join(', '));
      }
      
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Remove um feriado (exclusão lógica)
   */
  async deleteHoliday(id: number): Promise<void> {
    try {
      const response = await api.delete(`/holidays/${id}`) as ApiResponse<void>;
      
      if (!response.success) {
        throw new Error(response.message || 'Erro ao excluir feriado');
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Feriado não encontrado');
      }
      
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Busca feriados por tipo
   */
  async getHolidaysByType(tipo: string): Promise<Holiday[]> {
    try {
      const response = await api.get(`/holidays/tipo/${tipo}`) as ApiResponse<Holiday[]>;
      
      if (response.success && response.data) {
        return response.data;
      }
      
      throw new Error(response.message || 'Erro ao buscar feriados por tipo');
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Busca feriados ativos em um período
   */
  async getActiveHolidays(dataInicio: string, dataFim: string): Promise<Holiday[]> {
    try {
      const response = await api.get(`/holidays/ativos?dataInicio=${dataInicio}&dataFim=${dataFim}`) as ApiResponse<Holiday[]>;
      
      if (response.success && response.data) {
        return response.data;
      }
      
      throw new Error(response.message || 'Erro ao buscar feriados ativos');
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Erro interno do servidor'
      );
    }
  }
}

export default new HolidayService(); 